// Tranche CA closure: bulk move-in wizard.
//
// Operator pastes a CSV of new residents (or hand-edits rows in the
// inline grid), runs Dry-run to validate against the live DB, fixes
// errors, then commits. The commit uses a single Idempotency-Key so a
// double-click or flaky network doesn't double-create households.
//
// CSV columns expected (header row optional):
//   unit_number, building_name, household_label, moved_in_at,
//   resident_name, resident_email, resident_phone, relationship, invite
//
// One resident per CSV row; rows with the same (unit_number, building_name)
// pair are grouped into a single bulk-row before send. This keeps the
// CSV easy to author by hand while letting the backend create one
// Household per unit with N residents in it.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner, Label } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';

type Relationship = 'leaseholder' | 'occupant' | 'minor' | 'caregiver';

interface ResidentRow {
  name: string;
  email?: string | null;
  phone?: string | null;
  relationship: Relationship;
  invite: boolean;
}

interface BulkRow {
  unit_number: string;
  building_name?: string | null;
  household_label?: string | null;
  moved_in_at?: string | null;
  residents: ResidentRow[];
}

interface RowResult {
  row_index: number;
  status: 'created' | 'would_create' | 'error';
  household_id: string | null;
  resident_ids: string[];
  invite_count: number;
  errors: string[];
}

interface BulkResponse {
  data: {
    dry_run: boolean;
    summary: {
      rows_total: number;
      rows_ok: number;
      rows_error: number;
      households: number;
      residents: number;
      invites: number;
    };
    rows: RowResult[];
  };
}

const RELATIONSHIPS: Relationship[] = ['leaseholder', 'occupant', 'minor', 'caregiver'];

// Tiny RFC4180-ish CSV split: handles quoted fields with embedded
// commas. Not bullet-proof, but the only acceptable failure modes for
// a paste-from-spreadsheet flow are "fewer columns than expected"
// (caught by parsing) and "embedded newlines inside quotes" (operator
// just doesn't do that — every spreadsheet renders newlines as <br>
// inside cells anyway).
function splitCsvLine(line: string): string[] {
  // RFC 4180-ish: `""` inside a quoted field is the escape for a
  // literal `"`. Indexed access (not for-of) so we can peek ahead.
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    // eslint-disable-next-line security/detect-object-injection -- i is a bounded loop index
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

interface ParseOutput {
  rows: BulkRow[];
  rowSourceLines: number[][];
  errors: Array<{ line: number; message: string }>;
}

function parseCsv(text: string): ParseOutput {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], rowSourceLines: [], errors: [] };

  const header = splitCsvLine(lines[0]!).map((h) => h.toLowerCase());
  const hasHeader =
    header.includes('unit_number') || header.includes('resident_name');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const cols = hasHeader
    ? header
    : [
        'unit_number',
        'building_name',
        'household_label',
        'moved_in_at',
        'resident_name',
        'resident_email',
        'resident_phone',
        'relationship',
        'invite',
      ];
  const idxOf = (k: string) => cols.indexOf(k);

  const errors: ParseOutput['errors'] = [];
  // Group flat resident rows by (unit_number, building_name).
  const grouped = new Map<string, { row: BulkRow; sources: number[] }>();
  dataLines.forEach((raw, i) => {
    const lineNum = hasHeader ? i + 2 : i + 1;
    const parts = splitCsvLine(raw);
    const get = (k: string): string => {
      const j = idxOf(k);
      if (j < 0 || j >= parts.length) return '';
      // eslint-disable-next-line security/detect-object-injection -- j is bounded above
      return (parts[j] ?? '').trim();
    };
    const unit_number = get('unit_number');
    const resident_name = get('resident_name');
    if (!unit_number) {
      errors.push({ line: lineNum, message: 'unit_number is required' });
      return;
    }
    if (!resident_name) {
      errors.push({ line: lineNum, message: 'resident_name is required' });
      return;
    }
    const building = get('building_name') || null;
    const key = `${building ?? ''}::${unit_number}`.toLowerCase();
    const invite = /^(true|1|yes|y)$/i.test(get('invite'));
    let rel = get('relationship').toLowerCase() as Relationship;
    if (!RELATIONSHIPS.includes(rel)) rel = 'occupant';
    const resident: ResidentRow = {
      name: resident_name,
      email: get('resident_email') || null,
      phone: get('resident_phone') || null,
      relationship: rel,
      invite,
    };
    const moved = get('moved_in_at') || null;
    const moved_in_at = moved ? new Date(moved).toISOString() : null;
    const label = get('household_label') || null;

    const existing = grouped.get(key);
    if (existing) {
      existing.row.residents.push(resident);
      existing.sources.push(lineNum);
    } else {
      grouped.set(key, {
        row: {
          unit_number,
          building_name: building,
          household_label: label,
          moved_in_at,
          residents: [resident],
        },
        sources: [lineNum],
      });
    }
  });
  const rows: BulkRow[] = [];
  const rowSourceLines: number[][] = [];
  for (const { row, sources } of grouped.values()) {
    rows.push(row);
    rowSourceLines.push(sources);
  }
  return { rows, rowSourceLines, errors };
}

export function BulkMoveInPage() {
  const [csvText, setCsvText] = useState('');
  const parsed = useMemo(() => parseCsv(csvText), [csvText]);
  const [dryRunResult, setDryRunResult] = useState<BulkResponse['data'] | null>(null);
  const [committedResult, setCommittedResult] = useState<BulkResponse['data'] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'idle' | 'dry-run' | 'commit'>('idle');
  const { key: idemKey, rotate: rotateIdemKey } = useIdempotencyKey();

  async function runDry() {
    setError(null);
    setBusy('dry-run');
    setCommittedResult(null);
    try {
      const res = await apiClient.post<BulkResponse>(
        '/api/v1/residents/bulk-move-in?dry_run=true',
        { rows: parsed.rows },
      );
      setDryRunResult(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Dry-run failed');
      setDryRunResult(null);
    } finally {
      setBusy('idle');
    }
  }

  async function runCommit() {
    setError(null);
    setBusy('commit');
    try {
      const res = await apiClient
        .withIdempotencyKey(idemKey)
        .post<BulkResponse>('/api/v1/residents/bulk-move-in', {
          rows: parsed.rows,
        });
      setCommittedResult(res.data);
      setDryRunResult(null);
      // A successful commit closes the user-submitted intent — the
      // next batch is a new intent and MUST carry a fresh key. Without
      // this, an operator who commits batch A, edits the CSV, and
      // commits batch B without pressing Reset would have batch B's
      // request replayed as batch A's stored response.
      rotateIdemKey();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Commit failed');
    } finally {
      setBusy('idle');
    }
  }

  function startNewBatch() {
    setCommittedResult(null);
    setDryRunResult(null);
    setError(null);
  }

  function reset() {
    setCsvText('');
    setDryRunResult(null);
    setCommittedResult(null);
    setError(null);
    rotateIdemKey();
  }

  const canDryRun = parsed.rows.length > 0 && parsed.errors.length === 0 && busy === 'idle';
  const canCommit =
    dryRunResult !== null &&
    dryRunResult.summary.rows_error === 0 &&
    busy === 'idle' &&
    !committedResult;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span className="rounded-full bg-primary-container p-2 text-on-primary">
          <Icon name="group_add" />
        </span>
        <div className="flex-1">
          <h1 className="font-headline-sm text-headline-sm text-on-surface">
            Bulk move-in
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Move several households in at once. Paste a spreadsheet
            of residents below, run <strong>Dry-run</strong> to check
            for problems, fix anything that's flagged, then click
            <strong> Commit</strong> when every row is OK.
          </p>
        </div>
        <Button variant="ghost" onClick={reset}>
          Reset
        </Button>
      </header>

      <Card>
        <CardBody>
          <h2 className="font-title-md text-title-md text-on-surface mb-2">
            How this works
          </h2>
          <ol className="list-decimal list-inside font-body-md text-body-md text-on-surface-variant space-y-1 mb-4">
            <li>
              Paste your spreadsheet below. One row per <em>person</em>.
              When two people share a unit number, they're grouped into
              the same household automatically.
            </li>
            <li>
              Click <strong>Dry-run</strong>. We check every row against
              the live database and show you what would happen, without
              writing anything yet.
            </li>
            <li>
              Fix any rows that came back with errors, then click
              <strong> Commit</strong>. Households and invites are
              created in one go.
            </li>
          </ol>

          <Label htmlFor="bulk-csv">Paste your spreadsheet here</Label>
          <textarea
            id="bulk-csv"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`unit_number,building_name,household_label,moved_in_at,resident_name,resident_email,resident_phone,relationship,invite
4B,Tower B,The Andersons,2026-06-01,Anne Anderson,anne@example.com,,leaseholder,true
4B,Tower B,The Andersons,,Bob Anderson,,,occupant,false`}
            className="w-full h-48 font-mono text-sm rounded border border-outline bg-surface-container-low p-3"
            aria-describedby="bulk-csv-help"
          />
          <p
            id="bulk-csv-help"
            className="font-body-md text-body-md text-on-surface-variant mt-2"
          >
            A header row is optional. Without one, the columns are read
            in the order shown above. Set <code>invite</code> to
            <code> true</code> for any resident you want to email a
            sign-in link to.
          </p>
        </CardBody>
      </Card>

      {parsed.errors.length > 0 && (
        <Card>
          <CardBody>
            <h2 className="font-title-md text-title-md text-error mb-2">
              CSV parse errors
            </h2>
            <ul className="list-disc list-inside font-body-md text-body-md text-on-surface">
              {parsed.errors.map((e, i) => (
                <li key={i}>
                  Line {e.line}: {e.message}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-title-md text-title-md text-on-surface">
              Parsed rows ({parsed.rows.length})
            </h2>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={!canDryRun}
                onClick={runDry}
              >
                {busy === 'dry-run' ? <Spinner /> : <Icon name="science" />}
                <span className="ml-1">Dry-run</span>
              </Button>
              <Button variant="primary" disabled={!canCommit} onClick={runCommit}>
                {busy === 'commit' ? <Spinner /> : <Icon name="check" />}
                <span className="ml-1">Commit</span>
              </Button>
            </div>
          </div>

          {parsed.rows.length === 0 ? (
            <p className="font-body-md text-body-md text-on-surface-variant">
              Paste a CSV above to see the parsed rows.
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-outline-variant">
              <table className="w-full text-left font-body-md text-body-md">
                <thead className="bg-surface-container-low text-label-md font-label-md uppercase text-on-surface-variant">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Unit</th>
                    <th className="px-3 py-2">Household</th>
                    <th className="px-3 py-2">Residents</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {parsed.rows.map((r, idx) => {
                    const result =
                      committedResult?.rows.find((x) => x.row_index === idx) ??
                      dryRunResult?.rows.find((x) => x.row_index === idx) ??
                      null;
                    return (
                      <tr key={idx} className="bg-surface align-top">
                        <td className="px-3 py-2 text-on-surface-variant">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2">
                          <div>{r.unit_number}</div>
                          {r.building_name ? (
                            <div className="text-on-surface-variant text-sm">
                              {r.building_name}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div>{r.household_label ?? <em>(no label)</em>}</div>
                          {r.moved_in_at ? (
                            <div className="text-on-surface-variant text-sm">
                              {new Date(r.moved_in_at).toLocaleDateString()}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <ul className="space-y-1">
                            {r.residents.map((p, j) => (
                              <li key={j}>
                                <span className="font-title-sm text-title-sm">
                                  {p.name}
                                </span>
                                {p.email ? (
                                  <span className="text-on-surface-variant">
                                    {' '}
                                    · {p.email}
                                  </span>
                                ) : null}
                                {p.invite ? (
                                  <Badge tone="info" className="ml-2">
                                    invite
                                  </Badge>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td className="px-3 py-2">
                          <RowStatusBadge result={result} />
                          {result && result.errors.length > 0 && (
                            <ul className="mt-1 list-disc list-inside text-error text-sm">
                              {result.errors.map((e, ei) => (
                                <li key={ei}>{e}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-error" role="alert">
              {error}
            </p>
          </CardBody>
        </Card>
      )}

      {(dryRunResult || committedResult) && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              <h2 className="font-title-md text-title-md text-on-surface mb-2">
                {committedResult ? 'Commit result' : 'Dry-run summary'}
              </h2>
              <SummaryGrid summary={(committedResult ?? dryRunResult)!.summary} />
              {committedResult && committedResult.summary.rows_error === 0 && (
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Done. View the new households on the{' '}
                    <Link to="/households" className="text-primary hover:underline">
                      Households
                    </Link>{' '}
                    page.
                  </p>
                  <Button variant="secondary" onClick={startNewBatch}>
                    <Icon name="add" className="text-sm" />
                    <span className="ml-1">Start new batch</span>
                  </Button>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function RowStatusBadge({ result }: { result: RowResult | null }) {
  if (!result) return <Badge tone="neutral">pending</Badge>;
  if (result.status === 'error') return <Badge tone="danger">error</Badge>;
  if (result.status === 'would_create')
    return <Badge tone="info">would create</Badge>;
  return <Badge tone="success">created</Badge>;
}

function SummaryGrid({ summary }: { summary: BulkResponse['data']['summary'] }) {
  const cells: Array<{ label: string; value: number; tone?: 'danger' | 'success' }> = [
    { label: 'Rows total', value: summary.rows_total },
    { label: 'Rows OK', value: summary.rows_ok, tone: 'success' },
    { label: 'Rows with error', value: summary.rows_error, tone: 'danger' },
    { label: 'Households', value: summary.households },
    { label: 'Residents', value: summary.residents },
    { label: 'Invites queued', value: summary.invites },
  ];
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {cells.map((c) => (
        <div key={c.label} className="rounded border border-outline-variant p-3">
          <dt className="font-label-md text-label-md uppercase text-on-surface-variant">
            {c.label}
          </dt>
          <dd
            className={`font-title-md text-title-md ${
              c.tone === 'danger' && c.value > 0
                ? 'text-error'
                : c.tone === 'success'
                  ? 'text-on-surface'
                  : 'text-on-surface'
            }`}
          >
            {c.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
